import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Type,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, Subscription } from "rxjs";
import { NavigationService } from "src/app/services/navigation.service";
import { findStaticDashboard } from "../static-dashboards";

// Single route target for every client-side static dashboard.
//
// ":module/static" resolves here rather than to a specific component; the
// dashboard_key in the (encrypted) ds_key query param decides which component
// gets mounted, looked up in STATIC_DASHBOARDS. Adding a dashboard therefore
// needs no route of its own.
//
// The mounted component keeps reading cl_key / md_key / ds_key from the route
// itself: ngComponentOutlet builds it with this host's injector, so its
// ActivatedRoute is the very same one.
//
// Every arrival gets a NEWLY BUILT component, even when the same class is being
// mounted again — see mountedKey and the null-then-set in mount(). Two of this
// route's navigations look identical to ngComponentOutlet but are different
// screens to the user: the same dashboard's Live and History buttons (one class,
// `hist_flg` apart), and one bookmark of it opened after another. Left to the
// outlet's own change detection the instance would be reused for both, and the
// screen would come up wearing the last visit's expansion, sort, fullscreen and
// selection state. Rebuilding is what makes "a new page" mean a new page.
@Component({
    selector: "app-static-dashboard-host",
    template: `
    <ng-container *ngIf="component">
      <ng-container *ngComponentOutlet="component"></ng-container>
    </ng-container>

    <div class="sdh-unknown" *ngIf="!component && resolved">
      This dashboard is not available.
    </div>
  `,
    styles: [
        `
           .sdh-unknown {
             padding: 24px;
             font-family: "Poppins", sans-serif;
             font-size: 14px;
             color: #737d88;
           }
         `,
    ],
    standalone: false
})
export class StaticDashboardHostComponent implements OnInit, OnDestroy {
  component: Type<any> | null = null;
  // Distinguishes "no key resolved yet" from "key resolved, nothing matched",
  // so the fallback message never flashes during the first change detection.
  resolved = false;

  /**
   * Which screen is currently mounted: the dashboard, the view, and the bookmark.
   *
   * The route republishes its params for reasons that are not navigations, so this is
   * what tells a real move between screens from a repeat of the one already up —
   * without it every emission would tear the screen down and rebuild it mid-load.
   */
  private mountedKey: string | null = null;

  private reqSubcription: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Both halves are needed to name a screen: the dashboard and its view live in the
    // query params, the bookmark key in the path.
    this.reqSubcription.push(
      combineLatest([this.route.params, this.route.queryParams]).subscribe(
        ([p, params]: [any, any]) => {
          const decrypted = this.navigationService.decryptData(params);
          const key = [
            decrypted["ds_key"] ?? "",
            // Live vs History — the same component class, a different screen.
            decrypted["hist_flg"] ?? "",
            p["bk_mrk_key"] ?? "",
          ].join("|");

          if (key === this.mountedKey) return;
          this.mountedKey = key;
          this.mount(findStaticDashboard(decrypted["ds_key"])?.component ?? null);
        }
      )
    );
  }

  /**
   * Put `component` on screen as a fresh instance.
   *
   * Cleared and flushed before it is set, which is what forces the rebuild:
   * *ngComponentOutlet only acts when its input CHANGES, so assigning the same class
   * again would leave the existing instance — and all of its state — in place. The
   * intermediate detectChanges() is what makes the outlet destroy the old view before
   * the new class is handed to it.
   */
  private mount(component: Type<any> | null): void {
    // `resolved` goes back to false for the flush as well, so the "not available"
    // message cannot flash in the gap between the old instance being destroyed and
    // the new one being built — that gap is one change-detection pass with no
    // component, which is exactly the state the message tests for.
    this.component = null;
    this.resolved = false;
    this.cdr.detectChanges();

    this.component = component;
    this.resolved = true;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
  }
}
